import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Picker, PickerItem, ProgressCircle, Tab, TabList, TabPanel, Tabs } from "@react-spectrum/s2";
import type { Key } from 'react';
import { toast } from '../../utils/toast';
import { CCOotbAllowlistEntry, CCCustomAllowlistEntry } from '../../types';
import { fetchOrgOotbAllowlist, fetchOrgCustomAllowlist, updateOrgOotbAllowlist, updateOrgCustomAllowlist, fetchOotbAllowlistUnselectUsage, fetchCustomAllowlistUnselectUsage } from '../../services/api';
import { DataTable, ColumnDefinition } from '../common/DataTable';
import { UnselectUsageDialog, UnselectUsageRow } from '../common/UnselectUsageDialog';
import { ThirdPartyModelDisclaimer } from '../common/ThirdPartyModelDisclaimer';
import { useProviderMetadata } from '../../hooks/useProviderMetadata';
interface OotbAllowlistChange {
    configurationId: string;
    isAllowed: boolean;
}
interface CustomAllowlistChange {
    registryId: string;
    isAllowed: boolean;
}
interface PendingAllowlistSave {
    ootbChanges: OotbAllowlistChange[];
    customChanges: CustomAllowlistChange[];
}
export interface ModelAllowlistPanelProps {
    orgId: string;
}
type AllowlistTab = 'ootb' | 'custom';
function setsEqual(a: Set<string>, b: Set<string>): boolean {
    return a.size === b.size && Array.from(a).every((key)=>b.has(key));
}
/**
 * OOTB and Custom are independent selections (both pools can have
 * selections at once) — matches design-frames/org-detail.html's finalized
 * Model Allowlist tab, which supersedes the mode-exclusive design in
 * docs/specs/command-center-model-config-spec.md.
 *
 * Each pool is its own endpoint (GET .../allowlist/ootb|custom) that returns
 * every row already carrying its own is_allowed, so the checked state comes
 * straight off the response — no client-side join of a separate "selected
 * ids" list against a catalog. Saving POSTs only the rows whose is_allowed
 * changed (a delta), each pool independently.
 *
 * Reuses the same DataTable primitive as the Overview/Domains table
 * (DomainsOverviewTable) and Spectrum tabs for switching model pools.
 */ export const ModelAllowlistPanel: React.FC<ModelAllowlistPanelProps> = ({ orgId })=>{
    const { t } = useTranslation();
    const { providers } = useProviderMetadata();
    // Registry is the default/first tab here — this panel is now reached
    // directly via Command Center → Models (org detail "allowlist" section).
    const [activeTab, setActiveTab] = useState<AllowlistTab>('custom');
    const [ootbEntries, setOotbEntries] = useState<CCOotbAllowlistEntry[]>([]);
    const [customEntries, setCustomEntries] = useState<CCCustomAllowlistEntry[]>([]);
    // Baselines captured at load / last successful save. Each row's *current*
    // allowed state lives on the entry itself (entry.isAllowed) so that toggling
    // it changes the row's item identity — Spectrum's TableView caches cell
    // content per row item, so a separate selection Set would leave the in-cell
    // Yes/No Picker visually stale. The delta on save is entry.isAllowed vs these.
    const [savedOotbSelected, setSavedOotbSelected] = useState<Set<string>>(new Set());
    const [savedCustomSelected, setSavedCustomSelected] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    // Non-null only while the backend would actually reject the save (some
    // revoked row is still referenced by an active domain) — a pure blocker,
    // never a "confirm anyway" step, since apply_ootb_changes/
    // apply_custom_changes hard-reject (409) on the backend regardless of
    // what the client does here.
    const [blockedRows, setBlockedRows] = useState<UnselectUsageRow[] | null>(null);
    const [isLoadingUsage, setIsLoadingUsage] = useState(false);
    useEffect(()=>{
        let cancelled = false;
        setLoading(true);
        Promise.all([
            fetchOrgOotbAllowlist(orgId),
            fetchOrgCustomAllowlist(orgId)
        ]).then(([ootb, custom])=>{
            if (cancelled) return;
            const ootbAllowed = new Set(ootb.filter((e)=>e.isAllowed).map((e)=>e.configurationId));
            const customAllowed = new Set(custom.filter((e)=>e.isAllowed).map((e)=>e.registryId));
            setOotbEntries(ootb);
            setCustomEntries(custom);
            setSavedOotbSelected(ootbAllowed);
            setSavedCustomSelected(customAllowed);
            setLoading(false);
        }).catch((error: unknown)=>{
            if (cancelled) return;
            setLoading(false);
            toast.negative(error instanceof Error ? error.message : t('commandCenter.modelAllowlist.failedToLoad'));
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        orgId,
        t
    ]);
    const setOotbAllowed = useCallback((configurationId: string, allowed: boolean)=>{
        setOotbEntries((prev)=>prev.map((e)=>(// System-default configs are always allowed and read-only here.
                e.configurationId === configurationId && !e.isAllowlistLocked ? {
                    ...e,
                    isAllowed: allowed
                } : e)));
    }, []);
    const setCustomAllowed = useCallback((registryId: string, allowed: boolean)=>{
        setCustomEntries((prev)=>prev.map((e)=>(e.registryId === registryId ? {
                    ...e,
                    isAllowed: allowed
                } : e)));
    }, []);
    // Current allowed sets, derived from the (mutable) entries — used for the
    // dirty check and to advance the saved baseline after a successful save.
    const ootbAllowedNow = useMemo(()=>new Set(ootbEntries.filter((e)=>e.isAllowed).map((e)=>e.configurationId)), [
        ootbEntries
    ]);
    const customAllowedNow = useMemo(()=>new Set(customEntries.filter((e)=>e.isAllowed).map((e)=>e.registryId)), [
        customEntries
    ]);
    // Update allowlist is scoped to whichever tab is currently active — it
    // never saves (or warns about) the other pool's outstanding edits, so
    // switching tabs can't surface a confirm-removal row for a pool you
    // aren't even looking at. Each pool still keeps its own edits in memory
    // across tab switches (visible as dirty on that tab), you just save one
    // pool at a time, each from its own tab.
    const isDirty = activeTab === 'ootb' ? !setsEqual(ootbAllowedNow, savedOotbSelected) : !setsEqual(customAllowedNow, savedCustomSelected);
    // Provider is stored as its key (e.g. "azure_openai"); show the friendly
    // label from provider metadata, same as the Models page tables. Falls back
    // to the raw key if metadata hasn't loaded or lacks that provider.
    const providerLabel = useCallback((provider: string)=>providers.find((p)=>p.key === provider)?.label ?? provider, [
        providers
    ]);
    const commitSave = useCallback(async ({ ootbChanges, customChanges }: PendingAllowlistSave)=>{
        setIsSaving(true);
        try {
            await Promise.all([
                ootbChanges.length ? updateOrgOotbAllowlist(orgId, ootbChanges) : Promise.resolve(),
                customChanges.length ? updateOrgCustomAllowlist(orgId, customChanges) : Promise.resolve()
            ]);
            // Only advance the saved baseline for the pool this save actually hit
            // its own endpoint for (handleSave scopes ootbChanges/customChanges to
            // activeTab) — advancing the other pool's baseline here would mark its
            // outstanding edits "saved" without ever having called its endpoint,
            // silently discarding them.
            if (activeTab === 'ootb') {
                setSavedOotbSelected(new Set(ootbAllowedNow));
            } else {
                setSavedCustomSelected(new Set(customAllowedNow));
            }
            toast.positive(t('commandCenter.modelAllowlist.saved'));
        } catch (err) {
            // The backend still hard-rejects (409) a revoke that's still in use
            // even if this client-side check somehow missed it (race with a
            // domain being wired up between the check and the save) — surface
            // that failure too, and leave the selection dirty so the user can
            // retry, instead of a silent no-op that looks saved.
            toast.negative(err instanceof Error ? err.message : t('commandCenter.modelAllowlist.failedToSave'));
        } finally{
            setIsSaving(false);
        }
    }, [
        orgId,
        activeTab,
        ootbAllowedNow,
        customAllowedNow,
        t
    ]);
    const handleSave = useCallback(async ()=>{
        // Delta: only rows whose allowed state changed since load, and only for
        // the pool whose tab is currently active — the other pool's own
        // outstanding edits (if any) are left untouched until its tab is active
        // and Save is clicked there.
        const ootbChanges = activeTab === 'ootb' ? ootbEntries.filter((e)=>!e.isAllowlistLocked && e.isAllowed !== savedOotbSelected.has(e.configurationId)).map((e)=>({
                configurationId: e.configurationId,
                isAllowed: e.isAllowed
            })) : [];
        const customChanges = activeTab === 'custom' ? customEntries.filter((e)=>e.isAllowed !== savedCustomSelected.has(e.registryId)).map((e)=>({
                registryId: e.registryId,
                isAllowed: e.isAllowed
            })) : [];
        const ootbRevokes = ootbChanges.filter((c)=>!c.isAllowed);
        const customRevokes = customChanges.filter((c)=>!c.isAllowed);
        if (ootbRevokes.length === 0 && customRevokes.length === 0) {
            await commitSave({
                ootbChanges,
                customChanges
            });
            return;
        }
        // A revoke might still be referenced by an active domain — the backend
        // hard-rejects that (409), so check first and block the save entirely
        // rather than let it round-trip into a failure. Only revokes that are
        // actually in use block anything; an unused revoke saves immediately.
        setIsLoadingUsage(true);
        try {
            const [ootbUsage, customUsage] = await Promise.all([
                ootbRevokes.length ? fetchOotbAllowlistUnselectUsage(orgId, ootbRevokes.map((c)=>c.configurationId)) : Promise.resolve([]),
                customRevokes.length ? fetchCustomAllowlistUnselectUsage(orgId, customRevokes.map((c)=>c.registryId)) : Promise.resolve([])
            ]);
            const blockedOotb = ootbUsage.filter((entry)=>entry.usage.length > 0);
            const blockedCustom = customUsage.filter((entry)=>entry.usage.length > 0);
            if (blockedOotb.length === 0 && blockedCustom.length === 0) {
                await commitSave({
                    ootbChanges,
                    customChanges
                });
                return;
            }
            // configurationName is an admin-chosen label, not unique — two OOTB
            // rows can share one (e.g. two configurations both named the same
            // thing), so disambiguate by appending the underlying model, the same
            // way the allowlist table itself distinguishes rows via its separate
            // Model column.
            const ootbLabelById = new Map(ootbEntries.map((e)=>[
                    e.configurationId,
                    t('commandCenter.modelAllowlist.ootbLabel', {
                        name: e.configurationName,
                        model: e.model
                    })
                ]));
            const customLabelById = new Map(customEntries.map((e)=>[
                    e.registryId,
                    t('commandCenter.modelAllowlist.customLabel', {
                        model: e.model,
                        provider: providerLabel(e.provider)
                    })
                ]));
            const rows: UnselectUsageRow[] = [
                ...blockedOotb.flatMap((entry)=>entry.usage.map((u)=>({
                            key: `ootb:${entry.id}:${u.orgId}`,
                            cells: [
                                ootbLabelById.get(entry.id) ?? entry.id,
                                u.orgName,
                                u.domainCount
                            ]
                        }))),
                ...blockedCustom.flatMap((entry)=>entry.usage.map((u)=>({
                            key: `custom:${entry.id}:${u.orgId}`,
                            cells: [
                                customLabelById.get(entry.id) ?? entry.id,
                                u.orgName,
                                u.domainCount
                            ]
                        })))
            ];
            setBlockedRows(rows);
        } catch (err) {
            toast.negative(err instanceof Error ? err.message : t('commandCenter.modelAllowlist.failedToLoadUsage'));
        } finally{
            setIsLoadingUsage(false);
        }
    }, [
        orgId,
        activeTab,
        ootbEntries,
        customEntries,
        savedOotbSelected,
        savedCustomSelected,
        commitSave,
        providerLabel,
        t
    ]);
    const handleCloseBlockedDialog = useCallback(()=>{
        setBlockedRows(null);
    }, []);
    // Switching pools mid-review shouldn't carry a stale usage table forward —
    // the rows were computed for whichever pool(s) had revokes at the time
    // Save was clicked, not for "whatever tab is active now."
    const handleTabChange = useCallback((tab: AllowlistTab)=>{
        setActiveTab(tab);
        setBlockedRows(null);
    }, []);
    // A disabled React Spectrum Picker can't host a TooltipTrigger, so a locked
    // (system-default) row uses a native title on a wrapper span instead.
    const LOCKED_TOOLTIP = t('commandCenter.modelAllowlist.lockedTooltip');
    const ootbColumns: ColumnDefinition<CCOotbAllowlistEntry>[] = useMemo(()=>[
            {
                key: 'configurationName',
                label: t('commandCenter.modelAllowlist.columns.configurationName'),
                minWidth: 160,
                isRowHeader: true,
                render: (entry)=><span className="data-table-cell-primary" title={entry.configurationName}>{entry.configurationName}</span>
            },
            {
                key: 'model',
                label: t('commandCenter.modelAllowlist.columns.model'),
                minWidth: 80
            },
            {
                key: 'provider',
                label: t('commandCenter.modelAllowlist.columns.provider'),
                minWidth: 78,
                render: (entry)=><span>{providerLabel(entry.provider)}</span>
            },
            {
                key: 'allowed',
                label: t('commandCenter.modelAllowlist.columns.allowed'),
                width: 110,
                render: (entry)=>(entry.isAllowlistLocked ? (<span title={LOCKED_TOOLTIP} style={{
                        display: 'flex',
                        width: '100%',
                        cursor: 'not-allowed'
                    }}>
            <Picker aria-label={t('commandCenter.modelAllowlist.allowedLocked', {
                        name: entry.configurationName
                    })} styles={" Za171"} value="yes" isDisabled>
              <PickerItem key="yes" id="yes">{t('commandCenter.modelAllowlist.yes')}</PickerItem>
              <PickerItem key="no" id="no">{t('commandCenter.modelAllowlist.no')}</PickerItem>
            </Picker>
          </span>) : (<Picker aria-label={t('commandCenter.modelAllowlist.allowedRow', {
                        name: entry.configurationName
                    })} styles={" Za171"} value={entry.isAllowed ? 'yes' : 'no'} onChange={(key)=>setOotbAllowed(entry.configurationId, key === 'yes')}>
            <PickerItem key="yes" id="yes">{t('commandCenter.modelAllowlist.yes')}</PickerItem>
            <PickerItem key="no" id="no">{t('commandCenter.modelAllowlist.no')}</PickerItem>
          </Picker>))
            }
        ], [
        providerLabel,
        setOotbAllowed,
        LOCKED_TOOLTIP,
        t
    ]);
    const customColumns: ColumnDefinition<CCCustomAllowlistEntry>[] = useMemo(()=>[
            {
                key: 'model',
                label: t('commandCenter.modelAllowlist.columns.model'),
                minWidth: 100,
                isRowHeader: true,
                render: (entry)=><span className="data-table-cell-primary" title={entry.model}>{entry.model}</span>
            },
            {
                key: 'provider',
                label: t('commandCenter.modelAllowlist.columns.provider'),
                minWidth: 78,
                render: (entry)=><span>{providerLabel(entry.provider)}</span>
            },
            {
                key: 'allowed',
                label: t('commandCenter.modelAllowlist.columns.allowed'),
                width: 110,
                render: (entry)=>(<Picker aria-label={t('commandCenter.modelAllowlist.allowedRow', {
                        name: entry.model
                    })} styles={" Za171"} value={entry.isAllowed ? 'yes' : 'no'} onChange={(key)=>setCustomAllowed(entry.registryId, key === 'yes')}>
          <PickerItem key="yes" id="yes">{t('commandCenter.modelAllowlist.yes')}</PickerItem>
          <PickerItem key="no" id="no">{t('commandCenter.modelAllowlist.no')}</PickerItem>
        </Picker>)
            }
        ], [
        providerLabel,
        setCustomAllowed,
        t
    ]);
    if (loading) {
        return (<div className={" sd171 _Ca171 eb171 FDvO7ld171"}>
        <ProgressCircle aria-label={t('commandCenter.modelAllowlist.loading')} isIndeterminate size="L"/>
      </div>);
    }
    return (<div className={" sd171 _ta171 Ul171 ql171 Fb171 M6Neosc171"}>
      <Tabs aria-label={t('commandCenter.modelAllowlist.tabsAria')} styles={" _ub171 _vb171 _sb171 Ma171"} selectedKey={activeTab} onSelectionChange={(key: Key)=>handleTabChange(key as AllowlistTab)}>
        <TabList UNSAFE_className="fb-tabs" aria-label={t('commandCenter.modelAllowlist.tabListAria')}>
          <Tab id="custom">{t('commandCenter.modelAllowlist.tabRegistry')}</Tab>
          <Tab id="ootb">{t('commandCenter.modelAllowlist.tabConfigured')}</Tab>
        </TabList>
        <TabPanel id="custom" styles={" _ub171 _vb171 _sb171 Ma171"}>
          <div className={" sd171 _ta171 Fb171 Ma171 Tf171 _oa171"}>
            <DataTable<CCCustomAllowlistEntry> ariaLabel={t('commandCenter.modelAllowlist.customAriaLabel')} layout="fill" minHeight="size-1200" density="regular" items={customEntries} columns={customColumns} getRowKey={(entry)=>entry.registryId} emptyStateTitle={t('commandCenter.modelAllowlist.customEmptyTitle')} emptyStateDescription={t('commandCenter.modelAllowlist.customEmptyDescription')}/>
          </div>
        </TabPanel>
        <TabPanel id="ootb" styles={" _ub171 _vb171 _sb171 Ma171"}>
          <div className={" sd171 _ta171 Fb171 Ma171 Tf171 _oa171"}>
            <DataTable<CCOotbAllowlistEntry> ariaLabel={t('commandCenter.modelAllowlist.ootbAriaLabel')} layout="fill" minHeight="size-1200" density="regular" items={ootbEntries} columns={ootbColumns} getRowKey={(entry)=>entry.configurationId} emptyStateTitle={t('commandCenter.modelAllowlist.ootbEmptyTitle')} emptyStateDescription={t('commandCenter.modelAllowlist.ootbEmptyDescription')}/>
          </div>
        </TabPanel>
      </Tabs>

      <div className={" sd171 _Cb171"}>
        <Button variant="accent" onPress={handleSave} isPending={isSaving || isLoadingUsage} isDisabled={!isDirty}>
          {t('primitives.save')}
        </Button>
      </div>

      <UnselectUsageDialog isOpen={!!blockedRows} onOpenChange={(open)=>{
        if (!open) handleCloseBlockedDialog();
    }} title={t('commandCenter.modelAllowlist.blockedTitle')} description={t('commandCenter.modelAllowlist.blockedDescription')} columnLabels={[
        t('commandCenter.modelAllowlist.blockedColConfigModel'),
        t('commandCenter.modelAllowlist.blockedColOrg'),
        t('commandCenter.modelAllowlist.blockedColDomainCount')
    ]} rows={blockedRows ?? []} onClose={handleCloseBlockedDialog}/>

      <ThirdPartyModelDisclaimer/>
    </div>);
};

import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-2af0af3db39e823eecbe2179b7dbb30d92c11ddd6b0720dbf28502ac6c84b5fa.css";
import "macro-6e0aca53a35681127a4829dd779cf0f91a71d55c6da65245d53ca4802b7e4a51.css";
import "macro-a506550039ba540457053a9b7faf71c13581539d97fa73afa195612c24f97436.css";
import "macro-e0331648be6fa369228bb62bb4607a9a989af171fa57111f00917f0a3de3b34c.css";
import "macro-e0331648be6fa369228bb62bb4607a9a989af171fa57111f00917f0a3de3b34c.css";
import "macro-7445c09ace703927a2e0f1203b058ec3b6d7d985c931ddb82801eac724560080.css";
import "macro-e0331648be6fa369228bb62bb4607a9a989af171fa57111f00917f0a3de3b34c.css";
import "macro-7445c09ace703927a2e0f1203b058ec3b6d7d985c931ddb82801eac724560080.css";
import "macro-267537f0fccf0d75b72fa9cdb8f24a18a54432f3aab47583e78e45cd0ed81b4c.css";